// LoginForm.tsx
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { Input, Button, Typography, Layout, Alert } from "antd";
import {
  UserOutlined,
  LockOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import useSound from "use-sound";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../../../contexts/ThemeContext";
import { TwoFactorForm } from "./TwoFactorForm";
import { sanitizeAndStore } from "../../../shared/utils/sanitization";

const { Title, Text } = Typography;
const { Content } = Layout;

// Styled Components
const LoginContainer = styled(Layout).attrs<{ "data-mode": "dark" | "light" }>(
  (props) => ({
    "data-mode": props["data-mode"],
  })
)`
  min-height: 100vh;
  background-color: ${(props) =>
    props["data-mode"] === "dark" ? "#030303" : "#f0f2f5"};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(172, 73, 73, 0.15) 0%,
      rgba(172, 73, 73, 0) 70%
    );
    transform: rotate(-12deg);
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -50%;
    left: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(172, 73, 73, 0.1) 0%,
      rgba(172, 73, 73, 0) 70%
    );
    transform: rotate(15deg);
    z-index: 0;
  }
`;

const GlassMorphismEffect = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  background: radial-gradient(
    circle,
    rgba(172, 73, 73, 0.2) 0%,
    rgba(172, 73, 73, 0) 70%
  );
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.6;
  z-index: 1;
  pointer-events: none;
`;

const LoginContent = styled(Content)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
  z-index: 2;
`;

const LoginCard = styled(motion.div).attrs<{ "data-mode": "dark" | "light" }>(
  (props) => ({
    "data-mode": props["data-mode"],
  })
)`
  background-color: ${(props) =>
    props["data-mode"] === "dark"
      ? "rgba(20, 20, 20, 0.85)"
      : "rgba(255, 255, 255, 0.85)"};
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 2.5rem;
  width: 100%;
  max-width: 420px;
  box-shadow: ${(props) =>
    props["data-mode"] === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 8px 32px rgba(0, 0, 0, 0.1)"};
  border: 1px solid rgba(172, 73, 73, 0.1);
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const Logo = styled(motion.img)`
  max-width: 100%;
  height: auto;
  margin-bottom: 1.5rem;

  @media (max-width: 480px) {
    max-width: 80%;
  }
`;

const FormContainer = styled(motion.form)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputContainer = styled(motion.div)`
  width: 100%;
`;

const StyledInput = styled(Input).attrs<{ "data-mode": "dark" | "light" }>(
  (props) => ({
    "data-mode": props["data-mode"],
  })
)`
  background-color: ${(props) =>
    props["data-mode"] === "dark"
      ? "rgba(31, 31, 31, 0.7)"
      : "rgba(245, 245, 245, 0.7)"};
  border: 1px solid
    ${(props) => (props["data-mode"] === "dark" ? "#333" : "#e1e1e1")};
  height: 50px;
  border-radius: 8px;
  color: ${(props) => (props["data-mode"] === "dark" ? "#fff" : "#333")};

  .ant-input {
    background-color: transparent;
    color: ${(props) => (props["data-mode"] === "dark" ? "#fff" : "#333")};
  }

  .ant-input-prefix {
    margin-right: 12px;
    color: #ac4949;
  }
`;

const StyledPasswordInput = styled(Input.Password).attrs<{
  "data-mode": "dark" | "light";
}>((props) => ({
  "data-mode": props["data-mode"],
}))`
  background-color: ${(props) =>
    props["data-mode"] === "dark"
      ? "rgba(31, 31, 31, 0.7)"
      : "rgba(245, 245, 245, 0.7)"};
  border: 1px solid
    ${(props) => (props["data-mode"] === "dark" ? "#333" : "#e1e1e1")};
  height: 50px;
  border-radius: 8px;
  color: ${(props) => (props["data-mode"] === "dark" ? "#fff" : "#333")};

  .ant-input {
    background-color: transparent;
    color: ${(props) => (props["data-mode"] === "dark" ? "#fff" : "#333")};
  }

  .ant-input-prefix {
    margin-right: 12px;
    color: #ac4949;
  }

  .ant-input-password-icon {
    color: ${(props) => (props["data-mode"] === "dark" ? "#666" : "#999")};
    &:hover {
      color: #ac4949;
    }
  }
`;

const LoginButton = styled(motion(Button))`
  height: 50px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  background: #ac4949;
  border: none;
  box-shadow: 0 4px 12px rgba(172, 73, 73, 0.3);

  &:hover {
    background: #bc5959;
  }
`;

const ErrorContainer = styled(motion.div)`
  margin-bottom: 1rem;
`;

const StyledAlert = styled(Alert).attrs<{ "data-mode": "dark" | "light" }>(
  (props) => ({
    "data-mode": props["data-mode"],
  })
)`
  background-color: ${(props) =>
    props["data-mode"] === "dark"
      ? "rgba(68, 20, 20, 0.8)"
      : "rgba(255, 235, 235, 0.8)"};
  border: 1px solid #ac4949;
  border-radius: 8px;

  .ant-alert-message {
    color: ${(props) => (props["data-mode"] === "dark" ? "#fff" : "#ac4949")};
  }

  .ant-alert-description {
    color: ${(props) =>
      props["data-mode"] === "dark"
        ? "rgba(255, 255, 255, 0.8)"
        : "rgba(172, 73, 73, 0.8)"};
  }

  .anticon {
    color: ${(props) =>
      props["data-mode"] === "dark" ? "#e88a8a" : "#ac4949"};
  }
`;

// Animaciones
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2,
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      when: "afterChildren",
      staggerChildren: 0.1,
      staggerDirection: -1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
  exit: { y: -20, opacity: 0, transition: { duration: 0.2 } },
};

const logoVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 8,
      duration: 1.2,
    },
  },
  hover: {
    scale: 1.1,
    rotate: [0, -2, 2, -2, 0],
    transition: {
      duration: 0.7,
      ease: "easeInOut",
    },
  },
  floating: {
    y: [0, -15, 0],
    transition: {
      repeat: Infinity,
      duration: 3,
      ease: "easeInOut",
    },
  },
};

const successVariants = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
  exit: {
    scale: 1.1,
    opacity: 0,
    transition: {
      duration: 0.4,
      ease: "easeIn",
    },
  },
};

const errorVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 15,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
    },
  },
};

export const LoginForm: React.FC = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loginSuccess, setLoginSuccess] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [awaitingAccess, setAwaitingAccess] = useState<boolean>(false);
  const [requires2FA, setRequires2FA] = useState<boolean>(false);
  const [twoFactorData, setTwoFactorData] = useState<{
    userId: string;
    username: string;
    role: string;
  } | null>(null);
  const { loginMutation } = useAuth();
  const [playSuccess] = useSound("/sounds/success.mp3");
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError(null);
    setAwaitingAccess(false);
    setRequires2FA(false);
    setIsLoading(true);

    try {
      const response = await loginMutation.mutateAsync({
        username: username.trim(),
        password: password.trim(),
      });

      // Check if 2FA is required (for SUPERADMIN)
      if (response?.requires2FA) {
        setTwoFactorData({
          userId: response.data.userId,
          username: response.data.username,
          role: response.data.role,
        });
        setRequires2FA(true);
      } else if (response?.data?.token) {
        // Regular successful login
        playSuccess();
        setLoginSuccess(true);

        setTimeout(() => {
          if (response.data.user.role === "SUPERADMIN") {
            navigate("/super-admin");
          } else {
            navigate("/user-panel/");
          }
        }, 1500);
      }
    } catch (error: any) {
      console.error("[Login] Authentication failed", error);
      let errorMessage = "Error al iniciar sesión. Inténtalo de nuevo.";
      let isAwaitingAccess = false;

      if (error.response) {
        // Check if error is due to awaiting access
        if (
          error.response.status === 403 &&
          error.response.data?.awaitingAccess
        ) {
          isAwaitingAccess = true;
          errorMessage =
            "Tu cuenta está pendiente de aprobación por un administrador.";
        } else if (error.response.status === 401) {
          errorMessage =
            "Credenciales incorrectas. Por favor verifica tu usuario y contraseña.";

          // Mensaje específico para usuario desactivado
          if (error.response.data?.message === "Usuario desactivado") {
            errorMessage =
              "Tu cuenta ha sido desactivada. Por favor contacta al administrador.";
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }

      if (isAwaitingAccess) {
        // Set waiting state if user is awaiting access
        setLoginSuccess(false);
        setAwaitingAccess(true);
      } else {
        setLoginError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASuccess = (response: any) => {
if (response?.data?.token) {
  playSuccess();
  setLoginSuccess(true);

  // Store the token and user data from the 2FA response
  sanitizeAndStore("token", response.data.token.replace("Bearer ", ""));
  sanitizeAndStore("userId", response.data.user.id);
  sanitizeAndStore("adminId", response.data.user.id); // Añadir esta línea
  sanitizeAndStore("role", response.data.user.role);
  sanitizeAndStore("username", response.data.user.username);

  setTimeout(() => {
    navigate("/super-admin");
  }, 1500);
}
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setTwoFactorData(null);
    setUsername("");
    setPassword("");
  };

  return (
    <LoginContainer data-mode={theme}>
      <GlassMorphismEffect style={{ top: "20%", right: "15%" }} />
      <GlassMorphismEffect style={{ top: "60%", left: "10%" }} />
      <GlassMorphismEffect style={{ bottom: "10%", right: "30%" }} />

      <LoginContent>
        <AnimatePresence mode="wait">
          {loginSuccess ? (
            <motion.div
              key="success"
              variants={successVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100vh",
                background: theme === "dark" ? "#030303" : "#f0f2f5",
                zIndex: 1000,
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{
                  scale: 1,
                  transition: {
                    type: "spring",
                    stiffness: 100,
                    damping: 8,
                    delay: 0.2,
                  },
                }}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Logo
                  src={
                    theme === "dark"
                      ? "/logo_green.png"
                      : "/logo_green_black.png"
                  }
                  alt="Logo"
                  style={{ height: 80, maxWidth: "100%" }}
                />
              </motion.div>
            </motion.div>
          ) : awaitingAccess ? (
            <LoginCard
              key="awaiting-access"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              data-mode={theme}
            >
              <LogoContainer>
                <Logo
                  src={
                    theme === "dark"
                      ? "/logo_green.png"
                      : "/logo_green_black.png"
                  }
                  alt="Logo"
                  variants={logoVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                />
              </LogoContainer>

              <motion.div variants={itemVariants}>
                <Title
                  level={3}
                  style={{
                    textAlign: "center",
                    color: theme === "dark" ? "#ffffff" : "#333333",
                    marginBottom: "1rem",
                  }}
                >
                  Esperando autorización del admin
                </Title>
                <Text
                  style={{
                    display: "block",
                    textAlign: "center",
                    color: theme === "dark" ? "#999999" : "#666666",
                    marginBottom: "1.5rem",
                  }}
                >
                  Tu cuenta está pendiente de aprobación. Por favor, contacta al
                  administrador.
                </Text>
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={{ textAlign: "center" }}
              >
                <LoginButton
                  type="primary"
                  size="large"
                  onClick={() => {
                    setAwaitingAccess(false);
                    setUsername("");
                    setPassword("");
                  }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{ maxWidth: "200px", margin: "0 auto" }}
                >
                  Volver al inicio
                </LoginButton>
              </motion.div>
            </LoginCard>
          ) : requires2FA && twoFactorData ? (
            <LoginCard
              key="2fa-form"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              data-mode={theme}
            >
              <LogoContainer>
                <Logo
                  src={
                    theme === "dark"
                      ? "/logo_green.png"
                      : "/logo_green_black.png"
                  }
                  alt="Logo"
                  variants={logoVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                />
              </LogoContainer>

              <TwoFactorForm
                userId={twoFactorData.userId}
                username={twoFactorData.username}
                theme={theme}
                onSuccess={handle2FASuccess}
                onCancel={handle2FACancel}
              />
            </LoginCard>
          ) : (
            <LoginCard
              key="login-form"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              data-mode={theme}
            >
              <LogoContainer>
                <Logo
                  src={
                    theme === "dark"
                      ? "/logo_green.png"
                      : "/logo_green_black.png"
                  }
                  alt="Logo"
                  variants={logoVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  drag
                  dragConstraints={{
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                />
              </LogoContainer>

              <motion.div variants={itemVariants}>
                <Title
                  level={3}
                  style={{
                    textAlign: "center",
                    color: theme === "dark" ? "#ffffff" : "#333333",
                    marginBottom: "0.5rem",
                  }}
                >
                  Iniciar Sesión
                </Title>
                <Text
                  style={{
                    display: "block",
                    textAlign: "center",
                    color: theme === "dark" ? "#999999" : "#666666",
                    marginBottom: "2rem",
                  }}
                >
                  Ingresa tus credenciales para continuar
                </Text>
              </motion.div>

              <AnimatePresence>
                {loginError && (
                  <ErrorContainer
                    variants={errorVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <StyledAlert
                      message="Error de autenticación"
                      description={loginError}
                      type="error"
                      showIcon
                      icon={<CloseCircleOutlined />}
                      closable
                      onClose={() => setLoginError(null)}
                      data-mode={theme}
                    />
                  </ErrorContainer>
                )}
              </AnimatePresence>

              <FormContainer onSubmit={handleSubmit}>
                <InputContainer variants={itemVariants}>
                  <StyledInput
                    size="large"
                    prefix={<UserOutlined />}
                    placeholder="Nombre de usuario"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    data-mode={theme}
                  />
                </InputContainer>

                <InputContainer variants={itemVariants}>
                  <StyledPasswordInput
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-mode={theme}
                  />
                </InputContainer>

                <motion.div variants={itemVariants}>
                  <LoginButton
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={isLoading}
                    block
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Iniciar sesión
                  </LoginButton>
                </motion.div>
              </FormContainer>
            </LoginCard>
          )}
        </AnimatePresence>
      </LoginContent>
    </LoginContainer>
  );
};
